import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { AutocompleteInputComponent } from '@delmonicos/shared-design-system-react/src/components/autocomplete-input/autocomplete-input.component';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { CountryCode } from '@delmonicos/shared-design-system-react/src/components/country-code/country-code';
import { Divider } from '@delmonicos/shared-design-system-react/src/components/divider/divider';
import { TextInput } from '@delmonicos/shared-design-system-react/src/components/input/text-input';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { HeadingEnum } from '@delmonicos/shared-design-system-react/src/models/enums/heading.enum';
import { useCreateNetworkFormValidation } from '@delmonicos/shared-sdk/libs/cpms/src/libs/customers-service-lib/src/wrapper/inputs/networks/create-network.form-validation';
import { CreateNetworkModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/customers-service-lib/src/wrapper/inputs/networks/create-network.model';
import { format, parseISO } from 'date-fns';
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useCustomerAutocomplete } from '@app/feature/customer/use-customer-autocomplete';

import * as css from './network-create-form-step-1.module.scss';

const NetworkCreateFormStep1CustomerSelection = ({
  field,
  fieldState: { error },
}: {
  field: ControllerRenderProps<CreateNetworkModel, 'customerId'>;
  fieldState: ControllerFieldState;
}): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const { customerId } = useCreateNetworkFormValidation();

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const { search, setSearch, selectedItem, autocompleteItems } =
    useCustomerAutocomplete(field.value);

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <AutocompleteInputComponent
      label={t('delmonicos-shared-sdk.outputs-network.customer')}
      placeholder={t('delmonicos-shared-sdk.outputs-network.customer')}
      required={!!customerId.required}
      value={search}
      onChange={setSearch}
      onSearch={setSearch}
      items={autocompleteItems}
      selectedItem={selectedItem}
      onSelect={(selectedItemNext) => {
        field.onChange(selectedItemNext?.key ?? '');
      }}
      emptyResultText={t('global.no-result-found')}
      errorMessage={error?.message}
    />
  );
};

const NetworkCreateFormStep1InformationFragment = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const { control } = useFormContext<CreateNetworkModel>();
  const { name, description, customerId } = useCreateNetworkFormValidation();

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Card gap={GapEnum.L} direction="column" flex={1}>
      <AccentTitle
        as={HeadingEnum.H2}
        icon="server-06"
        text={t('$.network-create.information')}
        variant={AccentVariantEnum.Primary}
      />
      <Controller
        name="name"
        control={control}
        rules={name}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t('delmonicos-shared-sdk.outputs-network.name')}
            placeholder={t('delmonicos-shared-sdk.outputs-network.name')}
            required={!!name.required}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) => {
              field.onChange(e.target.value === '' ? '' : e.target.value);
            }}
          />
        )}
      />
      <Controller
        name="description"
        control={control}
        rules={description}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t('delmonicos-shared-sdk.outputs-network.description')}
            placeholder={t('delmonicos-shared-sdk.outputs-network.description')}
            required={!!description.required}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) => {
              field.onChange(e.target.value === '' ? '' : e.target.value);
            }}
          />
        )}
      />
      <Controller
        name="customerId"
        control={control}
        rules={customerId}
        render={NetworkCreateFormStep1CustomerSelection}
      />
    </Card>
  );
};

const NetworkCreateFormStep1InvoicingFragment = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const { control } = useFormContext<CreateNetworkModel>();

  const {
    holderFirstName,
    holderLastName,
    holderEmail,
    holderAddress,
    holderAddressCity,
    holderAddressCountryCode,
    holderAddressZipCode,
    holderCompanyName,
    holderVatNumber,
    holderSiret,
  } = useCreateNetworkFormValidation();

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Card gap={GapEnum.L} direction="column">
      <AccentTitle
        as={HeadingEnum.H2}
        icon="receipt-check"
        text={t('$.network-create.invoicing')}
        variant={AccentVariantEnum.Accent1}
      />

      <Stack gap={GapEnum.L} flexWrap="wrap">
        <Controller
          name="holder.lastName"
          control={control}
          rules={holderLastName}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-holder.last-name')}
              placeholder={t('delmonicos-shared-sdk.outputs-holder.last-name')}
              required={!!holderLastName.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) => {
                field.onChange(e.target.value === '' ? '' : e.target.value);
              }}
            />
          )}
        />
        <Controller
          name="holder.firstName"
          control={control}
          rules={holderFirstName}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-holder.first-name')}
              placeholder={t('delmonicos-shared-sdk.outputs-holder.first-name')}
              required={!!holderFirstName.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) => {
                field.onChange(e.target.value === '' ? '' : e.target.value);
              }}
            />
          )}
        />
      </Stack>
      <Controller
        name="holder.email"
        control={control}
        rules={holderEmail}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t('delmonicos-shared-sdk.outputs-holder.email')}
            placeholder={t('delmonicos-shared-sdk.outputs-holder.email')}
            required={!!holderEmail.required}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) => {
              field.onChange(e.target.value === '' ? '' : e.target.value);
            }}
          />
        )}
      />
      <Controller
        name="holder.companyName"
        control={control}
        rules={holderCompanyName}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t('delmonicos-shared-sdk.outputs-holder.company-name')}
            placeholder={t('delmonicos-shared-sdk.outputs-holder.company-name')}
            required={!!holderCompanyName.required}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) => {
              field.onChange(e.target.value === '' ? '' : e.target.value);
            }}
          />
        )}
      />
      <Controller
        name="holder.siret"
        control={control}
        rules={holderSiret}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t('delmonicos-shared-sdk.outputs-holder.siret')}
            placeholder={t('delmonicos-shared-sdk.outputs-holder.siret')}
            required={!!holderSiret.required}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) => {
              field.onChange(e.target.value === '' ? '' : e.target.value);
            }}
          />
        )}
      />
      <Controller
        name="holder.vatNumber"
        control={control}
        rules={holderVatNumber}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t('delmonicos-shared-sdk.outputs-holder.vat-number')}
            placeholder={t('delmonicos-shared-sdk.outputs-holder.vat-number')}
            required={!!holderVatNumber.required}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) => {
              field.onChange(e.target.value === '' ? '' : e.target.value);
            }}
          />
        )}
      />
      <Controller
        name="holder.address.address"
        control={control}
        rules={holderAddress}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t('delmonicos-shared-sdk.outputs-address.address')}
            placeholder={t('delmonicos-shared-sdk.outputs-address.address')}
            required={!!holderAddress.required}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) => {
              field.onChange(e.target.value === '' ? '' : e.target.value);
            }}
          />
        )}
      />
      <div className={css.zipCityContainer}>
        <Controller
          name="holder.address.zipCode"
          control={control}
          rules={holderAddressZipCode}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-address.zip-code')}
              placeholder={t('delmonicos-shared-sdk.outputs-address.zip-code')}
              required={!!holderAddressZipCode.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) => {
                field.onChange(e.target.value === '' ? '' : e.target.value);
              }}
            />
          )}
        />
        <Controller
          name="holder.address.city"
          control={control}
          rules={holderAddressCity}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-address.city')}
              placeholder={t('delmonicos-shared-sdk.outputs-address.city')}
              required={!!holderAddressCity.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) => {
                field.onChange(e.target.value === '' ? '' : e.target.value);
              }}
            />
          )}
        />
      </div>
      <Controller
        name="holder.address.countryCode"
        control={control}
        rules={holderAddressCountryCode}
        render={({ field, fieldState: { error } }) => (
          <CountryCode
            errorMessage={error?.message}
            fieldValue={field.value}
            fieldOnChange={field.onChange}
          />
        )}
      />
    </Card>
  );
};

const NetworkCreateFormStep1BankFragment = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const { control, trigger, watch } = useFormContext<CreateNetworkModel>();

  const sepaDateValue = watch('billingDetails.mandate.dateOfSignature');
  const sepaDocumentNumberValue = watch(
    'billingDetails.mandate.documentNumber',
  );

  const {
    billingIban,
    billingBic,
    billingMandateDocumentNumber,
    billingMandateDateOfSignature,
  } = useCreateNetworkFormValidation();

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Card gap={GapEnum.L} direction="column" flex={1}>
      <AccentTitle
        as={HeadingEnum.H2}
        icon="credit-card-01"
        text={t('$.network-create.bank')}
        variant={AccentVariantEnum.Accent2}
      />
      <Controller
        name="billingDetails.iban"
        control={control}
        rules={billingIban}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t('delmonicos-shared-sdk.outputs-billing-details.iban')}
            placeholder={t(
              'delmonicos-shared-sdk.outputs-billing-details.iban',
            )}
            required={!!billingIban.required}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) => {
              field.onChange(e.target.value === '' ? '' : e.target.value);
            }}
          />
        )}
      />
      <Controller
        name="billingDetails.bic"
        control={control}
        rules={billingBic}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t('delmonicos-shared-sdk.outputs-billing-details.bic')}
            placeholder={t('delmonicos-shared-sdk.outputs-billing-details.bic')}
            required={!!billingBic.required}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) => {
              field.onChange(e.target.value === '' ? '' : e.target.value);
            }}
          />
        )}
      />
      <Divider />
      <h3>{t('delmonicos-shared-sdk.outputs-billing-details.mandate')}</h3>
      <Controller
        name="billingDetails.mandate.documentNumber"
        control={control}
        rules={billingMandateDocumentNumber}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t(
              'delmonicos-shared-sdk.outputs-sepa-mandate.document-number',
            )}
            placeholder={t(
              'delmonicos-shared-sdk.outputs-sepa-mandate.document-number',
            )}
            required={!!sepaDateValue || !!sepaDocumentNumberValue}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) => {
              field.onChange(e.target.value === '' ? '' : e.target.value);
              trigger([
                'billingDetails.mandate.documentNumber',
                'billingDetails.mandate.dateOfSignature',
              ]);
            }}
          />
        )}
      />
      <Controller
        name="billingDetails.mandate.dateOfSignature"
        control={control}
        rules={billingMandateDateOfSignature}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t(
              'delmonicos-shared-sdk.outputs-sepa-mandate.date-of-signature',
            )}
            placeholder={t(
              'delmonicos-shared-sdk.outputs-sepa-mandate.date-of-signature',
            )}
            required={!!sepaDocumentNumberValue || !!sepaDateValue}
            errorMessage={error?.message}
            type="date"
            {...field}
            value={field.value ? format(field.value, 'yyyy-MM-dd') : ''}
            onChange={(e) => {
              field.onChange(e.target.value ? parseISO(e.target.value) : null);
              trigger([
                'billingDetails.mandate.documentNumber',
                'billingDetails.mandate.dateOfSignature',
              ]);
            }}
          />
        )}
      />
    </Card>
  );
};

export const NetworkCreateFormStep1Controller = (): React.JSX.Element => {
  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Stack gap={GapEnum.L}>
      <Stack gap={GapEnum.L} direction="column" flex={1} alignItems="stretch">
        <NetworkCreateFormStep1InformationFragment />
      </Stack>
      <Stack gap={GapEnum.L} direction="column" flex={1} alignItems="stretch">
        <NetworkCreateFormStep1InvoicingFragment />
      </Stack>
      <Stack gap={GapEnum.L} direction="column" flex={1} alignItems="stretch">
        <NetworkCreateFormStep1BankFragment />
      </Stack>
    </Stack>
  );
};
